/**
 * Sanitizes error messages for user-facing display.
 * Maps known Supabase/internal error patterns to friendly messages.
 */

const ERROR_PATTERNS: [RegExp, string][] = [
  [/seat limit reached/i, "You've reached your seat limit. Contact support to add seats."],
  [/invalid login credentials/i, 'Invalid email or password.'],
  [/email rate limit exceeded/i, 'Too many attempts. Please try again later.'],
  [/user already registered/i, 'An account with this email already exists.'],
  [/email not confirmed/i, 'Please confirm your email address first.'],
  [/new row violates row-level security/i, "You don't have permission to do this."],
  [/row-level security/i, "You don't have permission to do this."],
  [/42501/i, "You don't have permission to do this."],
  [/violates check constraint/i, 'Unable to process this request.'],
  [/violates foreign key/i, 'Unable to process this request.'],
  [/violates unique constraint/i, 'This record already exists.'],
  [/duplicate key value/i, 'This record already exists.'],
  [/not a super admin/i, 'Access denied.'],
  [/move or remove the people in/i, "Move or remove this IMO's people first — an IMO has no organization above it for them to join."],
  // Deactivation (20260929000002)
  [/owns an organization: deactivate the organization instead/i, 'This person owns an organization. Deactivate the organization instead.'],
  [/owns an organization: delete the organization instead/i, 'This person owns an organization. Delete the organization instead.'],
  [/can't do this to an organization you own/i, "You can't do this to an organization you own. Deactivate your own account from Settings."],
  [/deactivate your own account from settings/i, 'Deactivate your own account from Settings.'],
  [/platform staff cannot deactivate themselves/i, "Platform staff accounts can't be deactivated from the app."],
  [/organization is deactivated: reactivate the organization instead/i, 'Their organization is deactivated. Reactivate the organization instead.'],
  [/organization above .* is deactivated/i, 'The organization above this one is deactivated. Reactivate that one first.'],
  [/is already deactivated/i, 'This organization is already deactivated.'],
  [/is not deactivated|account is not deactivated/i, "This isn't deactivated."],
  [/only platform staff can delete/i, 'Only platform staff can delete. Deactivate instead.'],
  [/also owns an organization outside it/i, 'Someone in this organization also owns another organization. Move them out first.'],
  [/platform organization/i, 'Only the super admin can change or delete an organization owned by platform staff.'],
  [/only organizations under .* can be moved out|only people in .* can be placed/i, 'Only people and organizations inside it can be placed from here.'],
  [/pick a destination outside/i, 'Pick a destination outside the organization being deactivated or deleted.'],
  [/can't delete an organization you own/i, "You can't delete an organization you own."],
  [/tactical tax's own organizations can't be deleted/i, "Tactical Tax's own organizations can't be deleted from here."],
  [/owner also owns an organization outside it/i, "This organization's owner also owns another organization. Remove that one first."],
  [/^forbidden/i, "You don't have permission to do this."],
  [/protected account/i, 'This account is protected and can only be removed by the platform owner.'],
  [/organization owners manage their own account/i, 'Organization owners manage their own account in Settings.'],
  [/permission denied/i, "You don't have permission to do this."],
  [/session_expired/i, 'Your session has expired. Please sign in again.'],
  [/JWT expired/i, 'Your session has expired. Please sign in again.'],
  [/refresh_token_not_found/i, 'Your session has expired. Please sign in again.'],
  [/invalid.*token/i, 'Invalid or expired link.'],
  [/network/i, 'Network error. Please check your connection.'],
  [/fetch failed/i, 'Network error. Please check your connection.'],
];

function extractMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object' && 'message' in err) return String((err as { message: unknown }).message);
  return '';
}

export function sanitizeError(err: unknown): string {
  const raw = extractMessage(err);

  for (const [pattern, friendly] of ERROR_PATTERNS) {
    if (pattern.test(raw)) return friendly;
  }

  return 'Something went wrong. Please try again.';
}
